import { AppError } from "./errors.js";

export const TEAM_CHANNEL_KINDS = ["channel", "direct"] as const;
export type TeamChannelKind = (typeof TEAM_CHANNEL_KINDS)[number];

/**
 * A channel is a group. A direct is a personal chat with exactly two people.
 * Kind is stored on the row. The conversation name is never used to infer it.
 */

export interface TeamChannel {
	id: string;
	organisationId: string;
	kind: TeamChannelKind;
	name: string;
	createdBy: string;
	createdAt: Date;
	deletedAt: Date | null;
}

export interface TeamChannelMember {
	organisationId: string;
	channelId: string;
	userId: string;
	lastReadAt: Date | null;
	joinedAt: Date;
}

export interface TeamMessage {
	id: string;
	organisationId: string;
	channelId: string;
	authorId: string;
	body: string;
	createdAt: Date;
	editedAt: Date | null;
	deletedAt: Date | null;
}

export function teamMessageDeletedCopy(): string {
	return "This message was deleted.";
}

export function canEditTeamMessage(
	authorId: string,
	actorId: string,
	deletedAt: Date | string | null,
	mediaKind: string | null,
): boolean {
	return authorId === actorId && !deletedAt && !mediaKind;
}

export function canDeleteTeamMessage(
	authorId: string,
	actorId: string,
	deletedAt: Date | string | null,
): boolean {
	return authorId === actorId && !deletedAt;
}

/** Stable pair for a two-person direct channel. Order is not a privilege. */
export function directPairKey(left: string, right: string): string {
	return left < right ? `${left}:${right}` : `${right}:${left}`;
}

export function isTeamChannelKind(value: string): value is TeamChannelKind {
	return (TEAM_CHANNEL_KINDS as readonly string[]).includes(value);
}

export function isTeamGroup(kind: TeamChannelKind): boolean {
	return kind === "channel";
}

export function canInviteToTeamConversation(kind: TeamChannelKind): boolean {
	return kind === "channel";
}

export function teamConversationKindCopy(kind: TeamChannelKind): "Group" | "Direct" {
	return kind === "direct" ? "Direct" : "Group";
}

export function teamConversationPeopleCopy(kind: TeamChannelKind, memberCount: number): string {
	if (kind === "direct") return "Direct";
	return memberCount === 1 ? "Group · 1 member" : `Group · ${memberCount} members`;
}

/** A direct shows the other person. The stored channel name is only a fallback. */
export function teamDirectDisplayName(peerName: string | null | undefined, fallback: string): string {
	const name = peerName?.trim();
	return name || fallback;
}

export function teamConversationTitle(
	kind: TeamChannelKind,
	storedName: string,
	selfId: string,
	members: readonly { userId: string; fullName: string }[],
): string {
	if (kind !== "direct") return storedName;
	return teamDirectDisplayName(
		members.find((member) => member.userId !== selfId)?.fullName,
		storedName,
	);
}

export function teamGroupCreateValues(): { kind: "channel"; pairKey: null } {
	return { kind: "channel", pairKey: null };
}

export function teamDirectCreateValues(left: string, right: string): { kind: "direct"; pairKey: string } {
	assertTeamDirectPair(left, right);
	return { kind: "direct", pairKey: directPairKey(left, right) };
}

export function assertTeamDirectPair(left: string, right: string): void {
	if (left === right) throw AppError.validation("Choose another person for a direct message.");
}

export function assertCanInviteToTeamConversation(kind: string): void {
	if (kind === "direct") throw AppError.validation("A direct message has two people.");
	if (kind !== "channel") throw AppError.validation("Invite people to a group channel.");
}

export const TEAM_MEDIA_KINDS = ["photo", "video", "voice"] as const;
export type TeamMediaKind = (typeof TEAM_MEDIA_KINDS)[number];

export function isTeamMediaKind(value: string): value is TeamMediaKind {
	return (TEAM_MEDIA_KINDS as readonly string[]).includes(value);
}

export function teamMediaListCopy(kind: TeamMediaKind): "Photo" | "Video" | "Voice message" {
	if (kind === "video") return "Video";
	if (kind === "voice") return "Voice message";
	return "Photo";
}

export function teamLastMessageCopy(
	body: string | null,
	mediaKind: string | null,
	deletedAt?: Date | string | null,
): string | null {
	if (deletedAt) return null;
	const caption = body?.trim() ?? "";
	if (caption) return caption;
	if (isTeamMediaKind(mediaKind ?? "")) return teamMediaListCopy(mediaKind as TeamMediaKind);
	return null;
}
